import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Users, UserCog, Briefcase, Receipt, Car, ArrowRight, X } from "lucide-react";
import { adminApi } from "../../lib/api";

const TYPE_META = {
  customer: { label: "Customer", Icon: Users, color: "text-[#f28a16]" },
  client:   { label: "Client",   Icon: UserCog, color: "text-[#0f766e]" },
  vehicle:  { label: "Vehicle",  Icon: Car,     color: "text-[#0f766e]" },
  job:      { label: "Job",      Icon: Briefcase, color: "text-[#20282c]" },
  invoice:  { label: "Invoice",  Icon: Receipt, color: "text-[#111619]" },
};

/**
 * Cmd/Ctrl+K palette. Fetches /admin/search with a 180 ms debounce, renders
 * a keyboard-navigable results list, and closes on Enter / Esc / result click.
 * Mounted globally in `AdminLayout`; use the exported `useGlobalSearchHotkey`
 * hook there to bind the shortcut.
 */
export const GlobalSearchPalette = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const nav = useNavigate();

  // Debounced fetch — cancel via the closure `alive` flag
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      setCounts({});
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await adminApi.search(query, 30);
        if (!alive) return;
        setResults(data.results || []);
        setCounts(data.counts || {});
        setActive(0);
      } catch (e) {
        if (alive) setResults([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 180);
    return () => { alive = false; clearTimeout(timer); };
  }, [q, open]);

  // Focus on open + reset when closing
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ(""); setResults([]); setCounts({}); setActive(0);
    }
  }, [open]);

  // Ensure active row scrolls into view during arrow-key nav
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [active]);

  const grouped = useMemo(() => {
    const byType = {};
    results.forEach((r) => { (byType[r.type] ||= []).push(r); });
    // Fixed section order matches the sidebar
    return ["customer", "client", "vehicle", "job", "invoice"]
      .filter((t) => byType[t]?.length)
      .map((t) => ({ type: t, items: byType[t] }));
  }, [results]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  const go = (r) => {
    if (!r?.url) return;
    onClose();
    nav(r.url);
  };

  const onKey = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (!flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + flat.length) % flat.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center pt-24 px-4"
      onClick={onClose}
      data-testid="global-search-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="global-search-palette"
      >
        <div className="flex items-center gap-3 px-4 h-14 border-b border-[#eef2f4]">
          <Search size={18} className="text-[#3f4b52]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search customers, clients, vehicles, jobs, invoices…"
            className="flex-1 h-full outline-none text-sm bg-transparent"
            data-testid="global-search-input"
          />
          {loading && <Loader2 size={14} className="animate-spin text-[#3f4b52]" />}
          <button onClick={onClose} className="text-[#3f4b52] hover:text-[#111619]" aria-label="Close" data-testid="global-search-close">
            <X size={16} />
          </button>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
          {q.trim().length < 2 && (
            <EmptyHelp />
          )}
          {q.trim().length >= 2 && !loading && flat.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-[#3f4b52]" data-testid="global-search-empty">
              Nothing matched <b className="text-[#111619]">&ldquo;{q.trim()}&rdquo;</b>.<br />
              Try a reg number, customer code, job number, invoice number or a name.
            </div>
          )}
          {grouped.map((group) => {
            const meta = TYPE_META[group.type];
            const Icon = meta.Icon;
            return (
              <div key={group.type}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-display uppercase tracking-widest text-[#3f4b52]">
                  {meta.label}s · {group.items.length}
                </div>
                <ul>
                  {group.items.map((r) => {
                    const idx = flat.indexOf(r);
                    const isActive = idx === active;
                    return (
                      <li
                        key={`${r.type}-${r.id}`}
                        data-idx={idx}
                        data-testid={`gs-row-${r.type}-${r.badge || r.id}`}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => go(r)}
                        className={`px-4 py-2.5 cursor-pointer flex items-center gap-3 border-l-2 ${
                          isActive
                            ? "bg-[#fff7ed] border-[#f28a16]"
                            : "border-transparent hover:bg-[#f5f6f6]"
                        }`}
                      >
                        <Icon size={16} className={meta.color} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-display font-bold text-[#111619] truncate">
                            {r.title}
                          </div>
                          {r.subtitle && (
                            <div className="text-xs text-[#3f4b52] truncate">{r.subtitle}</div>
                          )}
                        </div>
                        {r.badge && (
                          <span className="font-mono text-[10px] font-bold text-[#f28a16] shrink-0">
                            {r.badge}
                          </span>
                        )}
                        <ArrowRight size={14} className="text-[#3f4b52] shrink-0" />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2.5 border-t border-[#eef2f4] bg-[#f5f6f6] flex items-center justify-between text-[11px] text-[#3f4b52]">
          <div className="flex items-center gap-4">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">Enter</kbd> open</span>
            <span><kbd className="font-mono">Esc</kbd> close</span>
          </div>
          <span className="uppercase font-display tracking-widest">Global search</span>
        </div>
      </div>
    </div>
  );
};

const EmptyHelp = () => (
  <div className="px-6 py-10 text-sm text-[#3f4b52]">
    <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-2">Search tips</div>
    <ul className="space-y-1 list-disc list-inside">
      <li>Customer <b>code</b> (e.g. <span className="font-mono">MYG001</span>) or name</li>
      <li>Vehicle <b>registration</b> or VIN</li>
      <li>Job number (e.g. <span className="font-mono">DDW-25-0142</span>) or client name</li>
      <li>Invoice number (e.g. <span className="font-mono">INV0017</span>)</li>
    </ul>
  </div>
);

/** Bind ⌘K / Ctrl+K globally to open the palette. */
export const useGlobalSearchHotkey = (setOpen) => {
  useEffect(() => {
    const onKey = (e) => {
      const key = (e.key || "").toLowerCase();
      if (!key) return;
      // Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // "/" opens too, unless the user is already typing in an input
      if (key === "/" && !(e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
};

export default GlobalSearchPalette;
